import { Grid, Typography, Button, Card, CardHeader, CardMedia, CardContent, CardActions, Box, CircularProgress, styled, useTheme } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import React, { useState, useContext, useEffect, useMemo } from "react";
import { Product } from "../admin.types";
import { SelectedPlantTypeContext } from "../AppContext";
import { PlantType, RestProduct } from "../Types";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Unstable_NumberInput as BaseNumberInput,
  NumberInputProps,
} from "@mui/base/Unstable_NumberInput";
import Drawer from "@mui/material/Drawer";
import LoadingButton from "@mui/lab/LoadingButton";
const ProductsDrawer = styled(Drawer)(({ theme }) => {
  return {
    [theme.breakpoints.down("md")]: {
      maxWidth: "85vw",
    },
    [theme.breakpoints.up("md")]: {
      maxWidth: "50vw",
    },
  };
});
export const SelectedPlantType = ({
  selectedPlantType,
}: {
  selectedPlantType: PlantType;
}) => {
  const [products, setProducts] = useState<Product[] | null>(null);
  const [shopifyProducts, setShopifyProducts] = useState<RestProduct[] | null>(null);
  const { getProducts, setSelectedPlantType, getShopifyProducts } = useContext(SelectedPlantTypeContext);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  useEffect(() => {
    getProducts(selectedPlantType.productIds.map((product) => product.legacyResourceId.toString()))
      .then((data) => {
        setProducts(data.products);
        getShopifyProducts(data.products.map((product) => product.handle)).then((shopifyProducts) => {
          setShopifyProducts(shopifyProducts?.filter((product) => product !== null) ?? null);
        }).catch((err) => {
          console.error(err);
        });
      }).catch((err) => {
        console.error(err);
      });
  }, []);
  let productEls: JSX.Element[] | JSX.Element = [];
  if (shopifyProducts !== null && shopifyProducts.length === 0) {
    productEls = (
      <Grid item sx={{ padding: 3 }} xs={12}>
        <Typography variant="h4">No related products found.</Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: "3rem",
          }}
        >
          <Button variant="outlined" onClick={() => setSelectedPlantType(null)}>Return to Browsing</Button>
        </Box>
      </Grid>
    );
  } else if (shopifyProducts !== null) {
    productEls = shopifyProducts.map((product) => {
      return (
        <Grid item sx={{ padding: 3 }} key={product.id} xs={12} md={6}>
          <ProductCard product={product} />
        </Grid>
      );
    });
  } else {
    productEls = <Grid item sx={{ padding: 3 }} xs={12}>
      <Typography variant="h4">Loading Related Products...</Typography>
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '3rem'}}>
        <CircularProgress />
      </Box>
    </Grid>;
  }
  return (
    <ProductsDrawer
      PaperProps={{
        sx: {
          [theme.breakpoints.down("md")]: {
            width: "85vw",
          },
          [theme.breakpoints.up("md")]: {
            width: "35vw",
          },
      }}}
      anchor="left"
      open={selectedPlantType !== null}
      onClose={() => {
        setSelectedPlantType(null);
      }}
    >
      <Grid
        container
        sx={{
          position: "relative",
          padding: "1rem",
        }}
      >
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          xs={12}
        >
          <Typography sx={{
            borderBottom: `3px solid ${theme.palette.primary.main}`,
          }} fontSize={"2.5rem"} variant="h3" textAlign="left">
            {selectedPlantType.name} Seeds
          </Typography>
          <Button
            endIcon={<CloseIcon />}
            onClick={() => {
              setSelectedPlantType(null);
            }}
          >
            Close
          </Button>
        </Grid>
        {productEls}
      </Grid>
      <Grid
        item
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
        xs={12}
      >
        <Button
          endIcon={<ChevronRightIcon />}
          onClick={() => {
            setSelectedPlantType(null);
          }}
        >
          Return to Browsing
        </Button>
      </Grid>
    </ProductsDrawer>
  );
};
const ProductCard = ({ product }: { product: RestProduct }) => {
  const variants = useMemo(() => {
    return product.variants;
  }, [product]);
  const { addToCart, currency } = useContext(SelectedPlantTypeContext);
  if (product.variants.length === 1) {
    console.log("Only one variant, selecting it.");
  }
  const [selectedVariant, setSelectedVariant] = useState<RestProduct['variants'][0] | null>(
    (product.variants.length === 1) ? product.variants[0] : null
  );
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({
    color: 'red',
    text: ''
  });
  const theme = useTheme();
  const selectEl = (
    <Box sx={{ display: variants.length > 1 ? "block" : "none" }}>
      <Typography textAlign={"center"} fontWeight="bold" variant="caption">
        OPTIONS:
      </Typography>
      <Grid container>
        {variants.map((variant) => {
          return (
            <Grid
              item
              sx={{
                padding: 1,
              }}
              key={variant.id}
              xs={6}
            >
              <Button
                key={variant.id}
                sx={{
                  width: "100%",
                  display: "block",
                  margin: "0 auto",
                }}
                variant={variant === selectedVariant ? "contained" : "outlined"}
                onClick={() => {
                  setSelectedVariant(variant);
                }}
              >{`${variant.title}\n${currency}${(variant.price / 100).toFixed(2)}`}</Button>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
  return (
    <Card
      elevation={4}
      sx={{
        minHeight: "fit-content",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        paddingBottom: 2,
      }}
    >
      <CardHeader
        sx={{ minHeight: "fit-content", marginBottom: '1em', height: "4em", backgroundColor: theme.palette.primary.main }}
        variant="h4"
        title={<Typography color={'white'} fontWeight={'bold'} fontSize={"1.25em"}>{product.title}</Typography>}
      />
      <CardMedia
        component="img"
        height="300"
        sx={{ objectFit: "contain" }}
        image={product.featured_image}
        title={product.title}
      />
      <CardContent sx={{ justifySelf: "flex-start" }}>{selectEl}</CardContent>
      <CardActions
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          justifySelf: "flex-end",
          marginLeft: 0,
        }}
      >
        <Typography textAlign={"center"} fontWeight="bold" variant="caption">
          QUANTITY
        </Typography>
        <QuantityInput quantity={quantity} enabled={!isLoading} setQuantity={(quantity) => setQuantity(quantity)} />
        <Box sx={{height: '2rem'}}>
          <Typography textAlign={"center"} fontWeight="bold" variant="caption" color={message.color}>
            {message.text}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-around",
            marginTop: 1,
            marginLeft: '0 !important',
          }}
        >
          <LoadingButton
            variant="contained"
            size="large"
            loading={isLoading}
            disabled={isLoading}
            onClick={() => {
              if (quantity === 0) {
                setMessage({
                  color: 'red',
                  text: `Please select a quantity to add to cart.`
                });
                return;
              }
              if (selectedVariant === null) {
                setMessage({
                  color: 'red',
                  text: `Please select a option to add to cart.`
                });
                return;
              }
              console.log(
                `Adding ${quantity} of ${selectedVariant?.title} to cart.`
              );
              const variantId = selectedVariant?.id;
              if (variantId === undefined) {
                console.log("id undefined");
                return;
              }
              const numberId = Number(variantId);
              const productId = Number(product.id);
              console.log("adding variant id", numberId);
              setIsLoading(true);
              addToCart(productId, numberId, quantity)
                .then((data) => {
                  setIsLoading(false);
                  if (data === null) {
                    setMessage({
                      color: "red",
                      text: `Failed to add ${quantity} item${
                        quantity > 1 ? "s" : ""
                      } to cart.`,
                    });
                    return;
                  }
                  setMessage({
                    color: 'green',
                    text: `Added ${quantity} item${quantity > 1 ? 's' : ''} to cart.`
                  });
                  setQuantity(1);
                  setSelectedVariant(null);
                  setTimeout(() => location.reload(), 1000);
                  console.log(data);
                })
                .catch((err) => {
                  setIsLoading(false);
                    setMessage({
                      color: "red",
                      text: `Failed to add ${quantity} item${
                        quantity > 1 ? "s" : ""
                      } to cart.`,
                    });
                  console.error(err);
                });
            }}
          >
            Add to Cart
          </LoadingButton>
          <Button
            sx={{ textAlign: "center", marginLeft: 1 }}
            href={`/products/${product.handle}`}
            variant="outlined"
            size="large"
          >
            Learn More
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
};
const NumberInput = React.forwardRef(function CustomNumberInput(
  props: NumberInputProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  return (
    <BaseNumberInput
      slots={{
        root: StyledInputRoot,
        input: StyledInput,
        incrementButton: StyledButton,
        decrementButton: StyledButton,
      }}
      slotProps={{
        incrementButton: {
          children: <AddIcon fontSize="small" />,
          className: "increment",
        },
        decrementButton: {
          children: <RemoveIcon fontSize="small" />,
        },
      }}
      {...props}
      ref={ref}
    />
  );
});

function QuantityInput({
  setQuantity,
  enabled,
  quantity,
}: {
    enabled: boolean;
  quantity: number;
  setQuantity: (quantity: number) => void;
}) {
  return (
    <NumberInput
      disabled={!enabled}
      aria-label="Quantity Input"
      min={0}
      max={99}
      defaultValue={0}
      value={quantity}
      onChange={(_1, v) => {
        setQuantity(v ?? 0);
      }}
    />
  );
}


const StyledInputRoot = styled("div")(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 400;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[500]};
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`
);

const StyledInput = styled("input")(
  ({ theme }) => `
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 400;
  line-height: 1.375;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
  box-shadow: 0px 2px 4px ${
    theme.palette.mode === "dark" ? "rgba(0,0,0, 0.5)" : "rgba(0,0,0, 0.05)"
  };
  border-radius: 8px;
  margin: 0 8px;
  padding: 10px 12px;
  outline: 0;
  min-width: 0;
  width: 4rem;
  text-align: center;

  &:hover {
    border-color: ${blue[400]};
  }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${
      theme.palette.mode === "dark" ? blue[700] : blue[200]
    };
  }

  &:focus-visible {
    outline: 0;
  }
`
);

const StyledButton = styled("button")(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  box-sizing: border-box;
  line-height: 1.5;
  border: 1px solid;
  border-radius: 999px;
  border-color: ${theme.palette.mode === "dark" ? grey[800] : grey[200]};
  background: ${theme.palette.mode === "dark" ? grey[900] : grey[50]};
  color: ${theme.palette.mode === "dark" ? grey[200] : grey[900]};
  width: 32px;
  height: 32px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  &:hover {
    cursor: pointer;
    background: ${theme.palette.mode === "dark" ? blue[700] : blue[500]};
    border-color: ${theme.palette.mode === "dark" ? blue[500] : blue[400]};
    color: ${grey[50]};
  }

  &:focus-visible {
    outline: 0;
  }

  &.increment {
    order: 1;
  }
`
);
